import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rm_admin_token";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  // null = checking, false = signed out, object = signed in
  const [admin, setAdmin] = useState(null);

  const readToken = () => {
    try {
      return localStorage.getItem(TOKEN_KEY) || "";
    } catch {
      return "";
    }
  };

  const persistToken = (t) => {
    try {
      if (t) localStorage.setItem(TOKEN_KEY, t);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  };

  // Verify token on mount; clear if invalid/expired.
  useEffect(() => {
    const token = readToken();
    if (!token) {
      setAdmin(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setAdmin({ ...res.data, token });
      } catch {
        if (cancelled) return;
        persistToken(null);
        setAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/admin/login`, { email, password });
    const { token, ...profile } = res.data;
    persistToken(token);
    setAdmin({ ...profile, token });
    return profile;
  }, []);

  const logout = useCallback(() => {
    persistToken(null);
    setAdmin(false);
  }, []);

  // Pre-built axios call with auth header attached.
  const adminAxios = useMemo(() => {
    const token = admin && typeof admin === "object" ? admin.token : null;
    if (!token) return null;
    const instance = axios.create({
      baseURL: API,
      headers: { Authorization: `Bearer ${token}` },
    });
    // Auto-logout on 401
    instance.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          persistToken(null);
          setAdmin(false);
        }
        return Promise.reject(err);
      },
    );
    return instance;
  }, [admin]);

  const value = useMemo(
    () => ({ admin, login, logout, adminAxios }),
    [admin, login, logout, adminAxios],
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
